// Copyright (c) StrangeBee 2024

import React, { ReactElement, useContext, useEffect, useMemo } from 'react';
import { useHistory, useParams, useRouteMatch } from 'react-router-dom';
import { DashboardView } from '@Organisms/Dashboard/DashboardView/DashboardView';
import { handleNotificationError, openMessage, useLocalMessage } from '@Helpers/';
import RouteBuilder from '@Services/RouteBuilder';
import { RouteType } from '@Types';
import client from '@Client';
import { computeEventList } from '@Organisms/Dashboard/eventList';
import useOneQuery from '@Hooks/useOneQuery';
import { DashboardOutput } from 'thehive-sdk';
import { computeGetSelector } from '@Organisms/Dashboard/selector';
import { ColorLabel } from '@Atoms/Info/ColorLabel/ColorLabel';
import { AutoLogoutContext } from '@Context/AutoLogoutContext';
import { useCurrentUser } from '@Queries/userQueries';
import usePageTitle from '@Hooks/usePageTitle';

import './DashboardDetails.scss';

const DashboardDetails = (): ReactElement => {
    const i = useLocalMessage();
    const history = useHistory();
    const { clearInactivityTimer, restartInactivityTimer } = useContext(AutoLogoutContext);
    const { data: currentUserData } = useCurrentUser();

    const { dashboardId } = useParams<{ dashboardId: string }>();
    const {
        data: dashboardData,
        isFetching,
        refetch,
        error,
    } = useOneQuery<DashboardOutput>({
        selector: computeGetSelector({ dashboardId }),
        controller: client.DashboardController,
    });

    useEffect(() => {
        currentUserData?.profile === 'read-only' && clearInactivityTimer();
        return () => {
            currentUserData?.profile === 'read-only' && restartInactivityTimer();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const eventList = useMemo(
        () =>
            computeEventList({
                controller: client.DashboardController,
                locale: i,
                onSuccess: (_, message) => {
                    message && openMessage('success', message);
                    refetch();
                },
                onError: handleNotificationError,
            }),
        [refetch, i],
    );

    useEffect(() => {
        if (error) {
            openMessage('error', 'Dashboard does not exist!');
            history.push(RouteBuilder.compute('dashboards.default'));
        }
    }, [error, history, i]);

    useEffect(() => {
        if (dashboardData?.version === 0) {
            openMessage('error', 'You cannot access this dashboard. Please convert it first!');
            history.push(RouteBuilder.compute('dashboards.default'));
        }
    }, [dashboardData, history]);

    usePageTitle(i('page-title.dashboards.details', { title: dashboardData?.title || '' }));

    return (
        <>
            {dashboardData && (
                <DashboardView
                    dashboardData={dashboardData}
                    isFetching={isFetching}
                    eventList={eventList}
                    refetch={refetch}
                />
            )}
        </>
    );
};

const BreadcrumbLabel = (): ReactElement => {
    const match = useRouteMatch('/dashboards/:dashboardId');

    const { dashboardId = '' } = (match?.params as any) || {};

    const { data: dashboardData } = useOneQuery<DashboardOutput>({
        selector: computeGetSelector({ dashboardId }),
        controller: client.DashboardController,
    });

    const { status, title } = dashboardData || {};

    return (
        <div className="dashboard-details__breadcrumb__title">
            <span>{title}</span>

            <ColorLabel label={status} color="lightgray" />
        </div>
    );
};

const path = RouteBuilder.compute('dashboards.details');

export const Route: RouteType = {
    path,
    breadcrumb: { label: <BreadcrumbLabel />, isLink: false },
    content: <DashboardDetails />,
    space: ['user'],
    exact: true,
};
